import { motion } from 'framer-motion';
import { Heart, Target, Eye, Award } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="About Us — Genius Academy Computer Training Institute in Kalaburagi"
        description="Founder-led, student-focused computer training institute in Kalaburagi (Gulbarga). 1:1 accountability, no bench-warmers, personal tracking for every student across MS Office, Tally, Python, Web Development, Photoshop, CorelDRAW and more."
        path="/about"
        image="https://geniusacademyklbg.com/images/computer-lab.webp"
        keywords="About Genius Academy, Computer Institute in Gulbarga, Computer Training Institute Kalaburagi, founder-led computer classes"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-4" data-testid="about-title">
              About <span className="text-cyan-600\">Genius Academy</span>
            </h1>
            <p className="text-lg text-cyan-600 font-semibold mb-6">
              Technical and Computer Training Institute
            </p>
            <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              We're not your typical computer training institute. We believe in personal accountability, 
              practical skills, and ensuring every student actually learns — not just attends.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/images/founder-teaching.png"
                alt="Indian teacher guiding a student at a computer at Genius Academy"
                className="rounded-2xl shadow-lg w-full h-auto object-cover"
                loading="lazy"
                width="1200"
                height="800"
                data-testid="about-founder-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Founder-Led, Student-Focused
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                At Genius Academy, I'm not just the owner — I'm your instructor. I started this institute 
                because I was frustrated with how most training centers operate: large batches, zero personal 
                attention, and students left behind.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Here, you're not just another enrollment number. I personally track every student's progress 
                daily. If you're struggling with a concept, we don't move forward until you master it. 
                That's my promise.
              </p>
              <p className="text-slate-700 leading-relaxed font-semibold">
                <strong className="text-cyan-600">No Bench-Warmers Policy:</strong> You're here to learn, 
                not just to attend. And I make sure that happens.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-700 leading-relaxed">
                To provide personalized computer training that focuses on actual skill development, 
                not just certificates. We empower students from Gulbarga and surrounding villages with 
                practical, job-ready skills through 1:1 accountability.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
              <p className="text-slate-700 leading-relaxed">
                To become the most trusted computer training institute in Karnataka by setting a new 
                standard: where every student is personally tracked, every concept is mastered, and 
                every graduate is truly job-ready.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Our Core Values
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Personal Care</h3>
              <p className="text-slate-600">
                Every student gets individual attention. You're not a number, you're a person with goals.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Results-Driven</h3>
              <p className="text-slate-600">
                We measure success by skills gained, not just courses completed. Your success is our success.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Excellence</h3>
              <p className="text-slate-600">
                We don't settle for 'good enough'. Mastery is the standard, and we help you reach it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visit Us — Signboard */}
      <section className="py-16 md:py-24 bg-slate-50" aria-labelledby="visit-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 id="visit-heading" className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Visit Our <span className="text-cyan-600">Institute</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Find us on <strong>MSK Mill Road (Central Bus Stand Road)</strong>, opposite Sangameshwar Hospital,
                Kalaburagi. Look for our board on the 2nd Floor of Darshan Orchid Building — you'll spot our full course
                lineup right at the entrance.
              </p>
              <ul className="space-y-2 text-slate-700 text-sm md:text-base">
                <li>📍 Darshan Orchid Building, MSK Mill Rd, Kalaburagi 585103</li>
                <li>📞 9164998997 / 9980147616</li>
                <li>🕒 Mon–Sat, 8:00 AM – 8:00 PM</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/outside-board.webp"
                alt="Genius Academy Technical and Computer Training Institute signboard, Darshan Orchid Complex, Kalaburagi"
                className="rounded-2xl shadow-lg w-full h-auto object-cover max-h-[520px]"
                width="1265"
                height="1687"
                loading="lazy"
                data-testid="about-signboard-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories — Certificate Distribution */}
      <section className="py-16 md:py-24" aria-labelledby="success-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:order-2"
            >
              <h2 id="success-heading" className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Real Students. <span className="text-cyan-600">Real Certificates.</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Every student who completes their course receives a Genius Academy certificate — a genuine
                recognition of the skills they've built through hands-on practice and personalised guidance.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Certificates are verifiable online via our <a href="/verify" className="text-cyan-600 font-semibold hover:underline">Certificate Verifier</a>,
                so your achievement is easy to share with employers and colleges.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:order-1"
            >
              <img
                src="/images/certificate-distribution.webp"
                alt="Certificate distribution ceremony at Genius Academy, Kalaburagi"
                className="rounded-2xl shadow-lg w-full h-auto object-cover"
                width="1265"
                height="949"
                loading="lazy"
                data-testid="about-certificate-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Experience Real Learning?
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Join us and see what 1:1 accountability can do for your skills.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-4 font-bold shadow-lg transition-all"
                data-testid="about-cta-contact"
              >
                Get Started
              </a>
              <a
                href="tel:9164998997"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold transition-all"
                data-testid="about-cta-call"
              >
                Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;